/**
 * Custom navigation extensions
 *
 * This file extends the auto-generated navigation with custom items
 * that are not part of the DSL (like Workflow Chaining, Business Rules)
 */

import { AccountTree, Checklist, Dashboard, EditNote, People, Rule, Security, SupervisorAccount, TableChart, Translate, TrendingUp, VisibilityOff } from '@mui/icons-material';
import {
  moduleNavigation as generatedNavigation,
  ModuleNavigationConfig,
  NavigationMenuItem
} from './navigation.generated';

/**
 * Custom navigation items to be merged into specific categories
 *
 * These items are NOT generated from entity DSL files - they are manually
 * defined views/features that need to appear in the navigation.
 */
export const customNavigationItems: Record<string, Record<string, NavigationMenuItem[]>> = {
  Finance: {
    // Bespoke plan-entry grid (sections × rows × period columns). Menu visible only with the
    // PlanEntry.View permission; saving cells requires PlanEntry.Edit (enforced on the endpoint).
    // Lives in the 'budget' column alongside the generated Plan* / Distribution entities.
    budget: [
      // The BudgetSetup hub (Setup-Flows spec, Phase 1): read-only readiness page, pinned at the
      // top of the Budget & Planning column — it is the entry point for the whole cycle.
      {
        id: 'budget-setup',
        label: 'Budget & Plan Readiness',
        labelKey: 'Menu:Finance.BudgetSetup',
        path: '/finance/setup/budget',
        icon: Checklist,
        order: 5,
        viewType: 'form',
        permission: 'Finance.SetupFlow.BudgetSetup',
      },
      {
        id: 'plan-entry',
        label: 'Plan Entry',
        labelKey: 'Menu:Finance.PlanEntry',
        path: '/finance/plan-entry',
        icon: EditNote,
        order: 10,
        viewType: 'form',
        permission: 'Finance.PlanEntry.View',
      },
      // Manager/controller view: consolidated org-tree budget. Gated on the dedicated ViewRollup
      // permission (distinct from editing one's own entry grid).
      {
        id: 'plan-rollup',
        label: 'Budget Consolidation',
        labelKey: 'Menu:Finance.PlanRollup',
        path: '/finance/plan-rollup',
        icon: AccountTree,
        order: 20,
        viewType: 'inquiry',
        permission: 'Finance.PlanEntry.ViewRollup',
      },
    ],
  },
  Core: {
    // Add to the 'configuration' category
    configuration: [
      {
        id: 'policies',
        label: 'Policies',
        labelKey: 'Menu:Core.Policies',
        path: '/core/policies',
        icon: Security,
        order: 10,
        viewType: 'form',
        permission: 'Core.Policy.View',
      },
      {
        id: 'dynamic-fields',
        label: 'Dynamic Fields',
        labelKey: 'Menu:Core.DynamicFields',
        path: '/core/dynamic-fields',
        icon: TableChart,
        order: 20,
        viewType: 'form',
        // The only menu item still on a Read permission: Core.DynamicField has no entity DSL
        // (the permission class is a leftover in CorePermissions.Generated.cs), so the generator
        // emits no ViewMenu for it. Give it one the day the entity is declared.
        permission: 'Core.DynamicField.Read',
      },
      // Both of these shipped without a `permission`, and Layout's filter reads a missing
      // permission as "visible to everyone" — a budget contributor saw them under
      // Configuration. Each now carries the permission its own API requires.
      // (Entity-backed items here use `{Module}.{Entity}.ViewMenu`, the same gate the generated
      // items get — menu visibility is never the entity's Read permission.)
      {
        id: 'workflow-chaining',
        label: 'Workflow Chaining',
        labelKey: 'Menu:Core.WorkflowChaining',
        path: '/core/workflow-chaining',
        icon: AccountTree,
        order: 30,
        viewType: 'form',
        permission: 'Core.WorkflowSubscription.Read',
      },
      {
        id: 'process-map',
        label: 'Process Map',
        labelKey: 'Menu:Core.ProcessMap',
        path: '/core/process-map',
        icon: AccountTree,
        order: 35,
        viewType: 'form',
        permission: 'Core.ProcessMap.View',
      },
      {
        id: 'field-restrictions',
        label: 'Field Restrictions',
        labelKey: 'Menu:Core.FieldRestrictions',
        path: '/core/fieldrestrictionsets',
        icon: VisibilityOff,
        order: 40,
        viewType: 'form',
        permission: 'Core.FieldRestrictionSet.ViewMenu',
      },
      {
        id: 'users',
        label: 'Users',
        labelKey: 'Menu:Core.Users',
        path: '/core/users',
        icon: People,
        order: 50,
        viewType: 'list',
        // ABP Identity built-in permission — the Users page calls /api/identity/users
        permission: 'AbpIdentity.Users',
      },
      {
        id: 'run-as-user',
        label: 'Run as user',
        labelKey: 'Menu:Core.RunAsUser',
        path: '/core/run-as',
        icon: SupervisorAccount,
        order: 55,
        viewType: 'list',
        // The impersonation permission ALONE (D14): a support session holds this
        // and nothing else, and this page is the only way it can reach the
        // action. Administrators hold it too and see the item as a shortcut.
        permission: 'Nexo.Users.Impersonate',
      },
      {
        id: 'roles',
        label: 'Roles',
        labelKey: 'Menu:Core.Roles',
        path: '/core/roles',
        icon: Security,
        order: 60,
        viewType: 'list',
        permission: 'AbpIdentity.Roles',
      },
      {
        id: 'translations',
        label: 'Translations',
        labelKey: 'Menu:Core.Translations',
        path: '/core/translations',
        icon: Translate,
        order: 70,
        viewType: 'form',
        permission: 'Core.Translations.Manage',
      },
    ],
    // Business Rules in the settings category. NOTE: a "Module Settings" entry belongs here
    // only when the module has a settings DSL (solution/docstudio/dsl/settings/{Module}.settings.yaml)
    // and a matching route — a menu entry without a page renders a blank content area.
    settings: [
      {
        id: 'business-rules',
        label: 'Business Rules',
        labelKey: 'Menu:Core.BusinessRules',
        path: '/core/businessrules',
        icon: Rule,
        order: 50,
        viewType: 'list',
        permission: 'Core.BusinessRule.ViewMenu',
      }
    ]
  },
  Sales: {
    // Dashboard
    inquiries: [
      {
        id: 'sales-dashboard-salesoverview',
        label: 'Sales Overview',
        labelKey: 'Menu:Sales.SalesOverview',
        path: '/sales/dashboard',
        icon: TrendingUp,
        order: 1,
        viewType: 'form' as const,
        permission: 'Sales.Dashboard.View',
      }
    ],
    // Add Module Settings to the 'settings' category
    settings: [
      {
        id: 'sales-settings',
        label: 'Module Settings',
        labelKey: 'Menu:Sales.ModuleSettings',
        path: '/sales/settings',
        order: 10,
        viewType: 'form',
        permission: 'Sales.Settings.Manage',
      }
    ]
  },
  Shared: {
    // Dashboard
    inquiries: [
      {
        id: 'shared-dashboard-global',
        label: 'Executive Dashboard',
        labelKey: 'Menu:Shared.ExecutiveDashboard',
        path: '/shared/dashboard',
        icon: Dashboard,
        order: 1,
        viewType: 'form' as const,
        permission: 'Dashboard.Global.View',
      }
    ]
  },
};

/**
 * Additional categories that might not be in the generated file
 * (because they have no entity-based items)
 */
const additionalCategories: Record<string, { id: string; label: string; labelKey?: string; icon: any; order: number }[]> = {
  Core: [
    { id: 'configuration', label: 'Configuration', icon: Security, order: 2 },
  ],
};

/**
 * Get combined navigation (generated + custom)
 */
export function getNavigationWithCustomItems(): ModuleNavigationConfig[] {
  const result = generatedNavigation.map(module => {
    const moduleCustomItems = customNavigationItems[module.module];
    const moduleAdditionalCategories = additionalCategories[module.module] || [];

    // Start with existing categories
    let categories = [...module.categories];

    // Add any missing categories
    for (const addCat of moduleAdditionalCategories) {
      if (!categories.find(c => c.id === addCat.id)) {
        categories.push({
          ...addCat,
          items: []
        });
      }
    }

    // Sort categories by order
    categories.sort((a, b) => a.order - b.order);

    if (!moduleCustomItems) {
      return { ...module, categories };
    }

    // Deep clone and merge custom items
    const updatedCategories = categories.map(category => {
      const customItems = moduleCustomItems[category.id];

      if (!customItems) {
        return category;
      }

      return {
        ...category,
        items: [...category.items, ...customItems].sort((a, b) => a.order - b.order)
      };
    });

    return {
      ...module,
      categories: updatedCategories
    };
  });

  // Append custom-only modules not in the generated navigation (e.g., "Shared")
  const generatedModuleNames = new Set(generatedNavigation.map(m => m.module));
  for (const [moduleName, categoryItems] of Object.entries(customNavigationItems)) {
    if (generatedModuleNames.has(moduleName)) continue;

    const categories = Object.entries(categoryItems).map(([catId, items]) => ({
      id: catId,
      label: catId.charAt(0).toUpperCase() + catId.slice(1),
      icon: (items[0]?.icon ?? Dashboard) as any,
      order: 0,
      items: items.sort((a, b) => a.order - b.order),
    }));

    result.push({
      module: moduleName,
      label: moduleName,
      icon: Dashboard as any,
      quickActions: [],
      categories,
    });
  }

  return result;
}

/**
 * Export combined navigation as default
 */
export const moduleNavigation = getNavigationWithCustomItems();

/**
 * Get navigation items for a specific module
 */
export function getModuleNavigation(moduleName: string) {
  return moduleNavigation.find(m => m.module === moduleName);
}

/**
 * Get all module names
 */
export function getAllModules() {
  return moduleNavigation.map(m => m.module);
}
